import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectIsAuthenticated } from "../authentication/authenticationSlice";
import {
  selectConsentStatus,
  selectConsentLoadingStatus,
  selectConsentAcceptStatus,
  selectConsentError,
} from "./instructor-consent-slice";
import {
  fetchConsentStatus,
  acceptConsent,
} from "./slice-extensions/instructor-consent-slice-extension";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ROUTES } from "../../routes";

/**
 * App-wide legal acceptance gate. While an authenticated instructor has not
 * accepted the current Terms of Service and Privacy Policy, a non-dismissable
 * full-screen overlay blocks all use of the product until they accept. New
 * sign-ups hit it on first login; existing instructors hit it again when a
 * document version changes. The server enforces the same rule on writes.
 */
/**
 * The legal-document routes the gate must NOT cover — otherwise an instructor
 * could never read the Terms / Privacy they are being asked to accept (the links
 * below open these in a new tab).
 */
const LEGAL_ROUTES: string[] = [
  ROUTES.TERMS_OF_SERVICE,
  ROUTES.PRIVACY_POLICY,
  ROUTES.MEMBER_TERMS_OF_SERVICE,
  ROUTES.MEMBER_PRIVACY_POLICY,
];

const ConsentGate = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const status = useAppSelector(selectConsentStatus);
  const loadingStatus = useAppSelector(selectConsentLoadingStatus);
  const acceptStatus = useAppSelector(selectConsentAcceptStatus);
  const error = useAppSelector(selectConsentError);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !status && loadingStatus === LoadingStatus.Idle) {
      dispatch(fetchConsentStatus());
    }
  }, [isAuthenticated, status, loadingStatus, dispatch]);

  if (
    !isAuthenticated ||
    !status ||
    !status.needsAcceptance ||
    LEGAL_ROUTES.includes(location.pathname)
  ) {
    return null;
  }

  const isSubmitting = acceptStatus === LoadingStatus.Loading;

  const handleAccept = () => {
    if (!accepted || isSubmitting) {
      return;
    }
    dispatch(acceptConsent());
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-primary-10/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-gate-title"
    >
      <div className="with-border w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl dark:bg-primary-10 lg:p-8">
        <h1
          id="consent-gate-title"
          className="text-2xl font-bold text-grey-20 dark:text-white"
        >
          {t("consentGate.title")}
        </h1>
        <p className="mt-3 leading-relaxed text-grey-30 dark:text-grey-70">
          {t("consentGate.body")}
        </p>

        <label className="mt-6 flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1 h-5 w-5 shrink-0 accent-primary-40"
          />
          <span className="text-sm text-grey-20 dark:text-grey-80">
            <Trans
              i18nKey="consentGate.agree"
              components={{
                termsLink: (
                  <a
                    href={ROUTES.TERMS_OF_SERVICE}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-40 dark:text-primary-90 hover:underline"
                  />
                ),
                privacyLink: (
                  <a
                    href={ROUTES.PRIVACY_POLICY}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-40 dark:text-primary-90 hover:underline"
                  />
                ),
              }}
            />
          </span>
        </label>

        {error && (
          <p className="mt-4 text-sm text-red-700 dark:text-red-400">
            {t("consentGate.error")}
          </p>
        )}

        <button
          type="button"
          onClick={handleAccept}
          disabled={!accepted || isSubmitting}
          className="btn btn-primary mt-6 w-full disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting
            ? t("consentGate.submitting")
            : t("consentGate.acceptButton")}
        </button>
      </div>
    </div>
  );
};

export default ConsentGate;
